import { Input } from '@builder.io/sdk';

/**
 * Generate a UUID that works outside secure contexts.
 *
 * `crypto.randomUUID` only exists when the page is a secure context (HTTPS or
 * localhost). The Builder preview is served over plain HTTP from a LAN IP, where
 * `crypto.randomUUID` is undefined and would throw at module load. Fall back to
 * `crypto.getRandomValues`, then to `Math.random`, so the editor never crashes.
 */
const generateId = (): string => {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }

  if (cryptoObj?.getRandomValues) {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;

    return value.toString(16);
  });
};

export const idField = {
  name: 'id',
  type: 'text',
  defaultValue: generateId(),
  showIf: 'false',
  helperText: 'Unique identifier for this block. Useful for anchor links or targeting with CSS/JS.',
} as const satisfies DeepReadonly<Input>;
